import Dialog from '@/components/dialog';
import type { GlobalPickFilesOptions } from '@/scripts/hooks/usePickFile';
import { useGlobalPickFiles } from '@/scripts/hooks/usePickFile';
import type { Chat } from '../useChats/types';
import { fileTransferService, TransferMode, TransferState } from './file-sdk';
import type {
  FileMessage,
  FileMessagePartial,
  FileMessagesStatePartial,
  PickInfo,
  PickInfoPartial,
} from './types';
import {
  type FileMessagesState,
  type FileMessagesActions,
  type FileInfo,
} from './types';
import { browserDownloadFile, generateThumbnails, genFileMessageState } from './utils';

const initState = genFileMessageState();

export function useFileMessages() {
  const [state, dispatch] = useReducer(
    (state: FileMessagesState, action: FileMessagesActions) => {
      let statePartial: FileMessagesStatePartial | undefined;

      switch (action.type) {
        case 'updateFileMessages': {
          const map = {
            ...state.map,
          };

          for (const k in action.payload) {
            const fm = { ...map[k], ...action.payload[k] };
            map[k] = fm;
          }

          statePartial = {
            map,
          };

          break;
        }

        case 'removeFileMessage': {
          const map = {
            ...state.map,
          };

          const result = delete map[action.payload];

          if (result) {
            statePartial = {
              map,
            };
          }

          break;
        }

        case 'getFileMessage': {
          action.cb(state.map[action.payload]);
          break;
        }

        case 'updatePickInfo': {
          const pickInfo = {
            ...state.pickInfo,
            ...action.payload,
          } as PickInfo;

          statePartial = {
            pickInfo,
          };

          break;
        }

        case 'removePickInfo': {
          statePartial = {
            pickInfo: undefined,
          };

          break;
        }
      }

      return statePartial ? { ...state, ...statePartial } : state;
    },
    initState
  );

  const pickFilesUtil = useGlobalPickFiles();

  const getFileMessage = useCallback((id: string) => {
    return new Promise<FileMessage | undefined>((resolve) =>
      dispatch({ type: 'getFileMessage', payload: id, cb: resolve })
    );
  }, []);

  const updateFileMessage = useCallback((id: string, fm: FileMessagePartial) => {
    dispatch({
      type: 'updateFileMessages',
      payload: {
        [id]: fm,
      },
    });
  }, []);

  const removeFileMessage = useCallback((id: string) => {
    dispatch({
      type: 'removeFileMessage',
      payload: id,
    });
  }, []);

  const genFileInfo = useCallback(async (file: File) => {
    const groups = /\.([^.]+)$/.exec(file.name);

    const fileInfo: FileInfo = {
      name: file.name,
      size: file.size,
      type: file.type,
      ext: groups ? groups[1] : '',
      width: 0,
      height: 0,
      thumbnail: '',
    };

    if (file.type.includes('image/')) {
      const result = await generateThumbnails(file);
      if (result) {
        const { data, originalWidth, originalHeight } = result;
        fileInfo.thumbnail = data;
        fileInfo.width = originalWidth;
        fileInfo.height = originalHeight;
      }
    }

    return fileInfo;
  }, []);

  const uploadFile = useCallback(
    async ({ id, file, info }: { id: string; file: File; info?: FileInfo }) => {
      info = info || (await genFileInfo(file));

      const abortController = new AbortController();

      updateFileMessage(id, {
        info,
        file,
        status: TransferState.ENCRYPTING,
        progress: 0,
        abortController,
      });

      try {
        const result = await fileTransferService.upload(file, {
          cancelSignal: abortController.signal,
          transferMode: TransferMode.SERVER, // TODO amo: P2P
          onProgress: (progress, status) => {
            updateFileMessage(id, {
              progress: progress / 100,
              status,
            });
          },
        });

        info.meta = JSON.stringify(result.meta);

        delete info.status;

        return info;
      } finally {
        removeFileMessage(id);
      }
    },
    [genFileInfo, removeFileMessage, updateFileMessage]
  );

  const downloadFile = useCallback(
    async ({ id, info }: { id: string; info: FileInfo }) => {
      const abortController = new AbortController();

      updateFileMessage(id, {
        info,
        status: TransferState.DECRYPTING,
        progress: 0,
        abortController,
      });

      try {
        const result = await fileTransferService.download(JSON.parse(info.meta), {
          cancelSignal: abortController.signal,
          onProgress: (progress, status) => {
            updateFileMessage(id, {
              progress: progress / 100,
              status,
            });
          },
        });

        if (result.data) {
          browserDownloadFile(result.data);

          message.success('Download completed');
        }
      } finally {
        removeFileMessage(id);
      }
    },
    [removeFileMessage, updateFileMessage]
  );

  const abortFile = useCallback(
    async (id: string) => {
      const fm = await getFileMessage(id);
      if (fm) {
        fm.abortController.abort();
      }
    },
    [getFileMessage]
  );

  const expireFile = useCallback(
    async (id: string) => {
      const fm = await getFileMessage(id);
      if (fm) {
        Dialog({
          title: 'The file has been destroyed and cannot be downloaded.',
          confirmText: 'Back',
          showCancel: false,
        });
        await abortFile(id);
        removeFileMessage(id);
      }
    },
    [abortFile, getFileMessage, removeFileMessage]
  );

  const updatePickInfo = useCallback((payload: PickInfoPartial) => {
    dispatch({
      type: 'updatePickInfo',
      payload,
    });
  }, []);

  const removePickInfo = useCallback(() => {
    dispatch({
      type: 'removePickInfo',
    });
  }, []);

  const getPickInfoEventHandlers = useCallback(
    (chat: Chat, options?: GlobalPickFilesOptions) => {
      const eventHandlers = pickFilesUtil.getEventHandlers(options);

      const updatePickInfo_ = () => {
        updatePickInfo({ chat, options });
      };

      const onClick_ = eventHandlers.click.onClick;
      const onDrop_ = eventHandlers.drop.onDrop;
      const onPaste_ = eventHandlers.paste.onPaste;

      eventHandlers.click.onClick = (...args) => {
        updatePickInfo_();
        return onClick_(...args);
      };

      eventHandlers.drop.onDrop = (...args) => {
        updatePickInfo_();
        return onDrop_(...args);
      };

      eventHandlers.paste.onPaste = (...args) => {
        updatePickInfo_();
        return onPaste_(...args);
      };

      return eventHandlers;
    },
    [pickFilesUtil, updatePickInfo]
  );

  useEffect(() => {
    if (pickFilesUtil.error) {
      message.error(pickFilesUtil.error.message);
    }
  }, [pickFilesUtil.error]);

  return useMemo(
    () => ({
      ...state,
      genFileInfo,
      uploadFile,
      updateFileMessage,
      removeFileMessage,
      getFileMessage,
      downloadFile,
      expireFile,
      abortFile,
      pickFilesUtil,
      updatePickInfo,
      removePickInfo,
      getPickInfoEventHandlers,
    }),
    [
      state,
      genFileInfo,
      uploadFile,
      updateFileMessage,
      removeFileMessage,
      getFileMessage,
      downloadFile,
      expireFile,
      abortFile,
      pickFilesUtil,
      updatePickInfo,
      removePickInfo,
      getPickInfoEventHandlers,
    ]
  );
}
